import { yupResolver } from '@hookform/resolvers/yup'
import { t, Trans } from '@lingui/macro'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Stack, TextField } from '@mui/material'
import { OrganizationMetadataCreateDTO, OrganizationMetadataPatchDTO } from '@om1/falcon-api'
import { useUserPermissions } from '@om1/platform-authentication/auth0/hooks/useUserPermissions'
import { RoutedFrameworkComponentProps } from '@om1/platform-utils'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { dataDeliveryActions, OrganizationMetadataState } from '../state/data-delivery'
import { S3CommonFields } from './S3CommonFields'

type OrganizationMetadataFormValues = {
    deliveryType: 'DATA_STUDIO' | 'S3_INTERNAL' | 'S3_EXTERNAL'
    // Data Studio fields
    deliveryDatabase: string
    deliveryShareName: string
    // Internal S3 fields
    deliveryAwsInternalS3BucketArn: string
    deliveryAwsInternalSftpPubKey: string
    deliveryAwsInternalRootDirectory: string
    deliveryAwsInternalRegion: string

    // External S3 fields
    deliveryAwsExternalS3BucketArn: string
    deliveryAwsExternalIamArn: string
    deliveryAwsExternalRootDirectory: string
    deliveryAwsExternalRegion: string
}

export type OrganizationMetadataSaveValues = OrganizationMetadataFormValues & { id?: string }

export type CreateEditOrganizationMetadataDialogProps = RoutedFrameworkComponentProps<
    {},
    {},
    { organizationMetadata: OrganizationMetadataState },
    Pick<typeof dataDeliveryActions, 'getOrganizationMetadata' | 'createOrganizationMetadata' | 'updateOrganizationMetadata'>,
    {
        onCancel: () => void
        onSave: () => void
        initialDeliveryType?: 'DATA_STUDIO' | 'S3_INTERNAL' | 'S3_EXTERNAL'
    }
>

/**
 * A dialog to edit the metadata fields for an organization's data delivery.
 */
export const CreateEditOrganizationMetadataDialogComponent = ({
    state,
    props: { onCancel, onSave, initialDeliveryType },
    actions
}: CreateEditOrganizationMetadataDialogProps) => {
    const { organizationMetadata } = state
    const { user } = useUserPermissions()

    // Define step types
    type StepType = 'connectionType' | 'connectionDetails'

    // Initialize step based on whether we have an initial delivery type
    const [step, setStep] = useState<StepType>(initialDeliveryType ? 'connectionDetails' : 'connectionType')

    // Always use current metadata from Redux state - single source of truth
    const formData = organizationMetadata.metadata

    const validationSchema = Yup.object().shape({
        deliveryType: Yup.string().required(t`Delivery type is required`),
        // Data Studio validation
        deliveryDatabase: Yup.string().when('deliveryType', {
            is: 'DATA_STUDIO',
            then: (schema) => schema.required(t`Database is required`)
        }),
        deliveryShareName: Yup.string().when('deliveryType', {
            is: 'DATA_STUDIO',
            then: (schema) => schema.required(t`Share is required`)
        }),
        // Internal S3 validation
        deliveryAwsInternalS3BucketArn: Yup.string().when('deliveryType', {
            is: 'S3_INTERNAL',
            then: (schema) => schema.required(t`Bucket ARN is required`)
        }),
        deliveryAwsInternalSftpPubKey: Yup.string().when('deliveryType', {
            is: 'S3_INTERNAL',
            then: (schema) => schema.required(t`SFTP Public Key is required`)
        }),
        deliveryAwsInternalRootDirectory: Yup.string().when('deliveryType', {
            is: 'S3_INTERNAL',
            then: (schema) => schema.optional()
        }),
        deliveryAwsInternalRegion: Yup.string().when('deliveryType', {
            is: 'S3_INTERNAL',
            then: (schema) => schema.required(t`Region is required`)
        }),

        // External S3 validation
        deliveryAwsExternalS3BucketArn: Yup.string().when('deliveryType', {
            is: 'S3_EXTERNAL',
            then: (schema) => schema.required(t`Bucket ARN is required`)
        }),
        deliveryAwsExternalIamArn: Yup.string().when('deliveryType', {
            is: 'S3_EXTERNAL',
            then: (schema) => schema.required(t`IAM ARN is required`)
        }),
        deliveryAwsExternalRootDirectory: Yup.string().when('deliveryType', {
            is: 'S3_EXTERNAL',
            then: (schema) => schema.optional()
        }),
        deliveryAwsExternalRegion: Yup.string().when('deliveryType', {
            is: 'S3_EXTERNAL',
            then: (schema) => schema.required(t`External Region is required`)
        })
    })

    const handleCancel = () => onCancel()
    const onSubmit: SubmitHandler<OrganizationMetadataFormValues> = (formData: OrganizationMetadataFormValues) => {
        const dto: OrganizationMetadataCreateDTO | OrganizationMetadataPatchDTO = {
            deliveryDatabase: formData.deliveryType === 'DATA_STUDIO' ? formData.deliveryDatabase : null,
            deliveryShareName: formData.deliveryType === 'DATA_STUDIO' ? formData.deliveryShareName : null,
            // Internal S3 fields - preserve all internal data regardless of current selection
            deliveryAwsInternalS3BucketArn: formData.deliveryAwsInternalS3BucketArn || null,
            deliveryAwsInternalSftpPubKey: formData.deliveryAwsInternalSftpPubKey || null,
            deliveryAwsInternalRootDirectory: formData.deliveryAwsInternalRootDirectory || null,
            deliveryAwsInternalRegion: formData.deliveryAwsInternalRegion || null,

            // External S3 fields - preserve all external data regardless of current selection
            deliveryAwsExternalS3BucketArn: formData.deliveryAwsExternalS3BucketArn || null,
            deliveryAwsExternalIamArn: formData.deliveryAwsExternalIamArn || null,
            deliveryAwsExternalRootDirectory: formData.deliveryAwsExternalRootDirectory || null,
            deliveryAwsExternalRegion: formData.deliveryAwsExternalRegion || null
        }

        if (organizationMetadata.metadata.id) {
            actions.updateOrganizationMetadata({
                organizationMetadataId: organizationMetadata.metadata.id,
                dto: dto as OrganizationMetadataPatchDTO
            })
        } else {
            if (user && user.org_id) {
                actions.createOrganizationMetadata({
                    ...(dto as OrganizationMetadataCreateDTO),
                    organizationId: user.org_id
                })
            }
        }

        onSave()
    }

    const { handleSubmit, control, watch } = useForm<OrganizationMetadataFormValues>({
        defaultValues: {
            deliveryType: initialDeliveryType || 'DATA_STUDIO',
            // Data Studio fields
            deliveryDatabase: formData.deliveryDatabase || '',
            deliveryShareName: formData.deliveryShareName || '',
            // Internal S3 fields - only set defaults if we have existing data or are editing S3
            deliveryAwsInternalS3BucketArn: formData.deliveryAwsInternalS3BucketArn || '',
            deliveryAwsInternalSftpPubKey: formData.deliveryAwsInternalSftpPubKey || '',
            deliveryAwsInternalRootDirectory: formData.deliveryAwsInternalRootDirectory || '',
            deliveryAwsInternalRegion: formData.deliveryAwsInternalRegion || '',

            // External S3 fields - only set defaults if we have existing data or are editing S3
            deliveryAwsExternalS3BucketArn: formData.deliveryAwsExternalS3BucketArn || '',
            deliveryAwsExternalIamArn: formData.deliveryAwsExternalIamArn || '',
            deliveryAwsExternalRootDirectory: formData.deliveryAwsExternalRootDirectory || '',
            deliveryAwsExternalRegion: formData.deliveryAwsExternalRegion || ''
        },
        resolver: yupResolver(validationSchema)
    })

    const deliveryType = watch('deliveryType')

    // Function to check if current delivery type has existing data
    const hasExistingDataForType = (type: string) => {
        switch (type) {
            case 'DATA_STUDIO':
                return !!(formData.deliveryDatabase || formData.deliveryShareName)
            case 'S3_INTERNAL':
                return !!(
                    formData.deliveryAwsInternalS3BucketArn ||
                    formData.deliveryAwsInternalSftpPubKey ||
                    formData.deliveryAwsInternalRootDirectory ||
                    formData.deliveryAwsInternalRegion
                )
            case 'S3_EXTERNAL':
                return !!(
                    formData.deliveryAwsExternalS3BucketArn ||
                    formData.deliveryAwsExternalIamArn ||
                    formData.deliveryAwsExternalRootDirectory ||
                    formData.deliveryAwsExternalRegion
                )
            default:
                return false
        }
    }

    // Add effect to monitor form changes

    const handleNext = () => {
        setStep('connectionDetails')
    }

    const handleBack = () => {
        setStep('connectionType')
    }

    // Create a unique key that changes when the dialog opens with new data
    // This forces the form to re-initialize with the latest metadata
    const metadataHash = JSON.stringify(organizationMetadata.metadata)
    const formKey = `${organizationMetadata.metadata.id || 'new'}-${initialDeliveryType || 'none'}-${metadataHash}`

    // Helper function to get the appropriate title
    const getDialogTitle = () => {
        if (step === 'connectionType') {
            return t`Select Connection Type`
        }

        switch (deliveryType) {
            case 'DATA_STUDIO':
                return t`Data Studio Configuration`
            case 'S3_INTERNAL':
                return t`S3 Internal Delivery`
            case 'S3_EXTERNAL':
                return t`S3 External Delivery`
            default:
                return t`Configuration`
        }
    }

    return (
        <Dialog open maxWidth='sm' fullWidth onClose={handleCancel}>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <form key={formKey} onSubmit={handleSubmit(onSubmit)} data-testid='form'>
                <DialogContent>
                    <Box>
                        <Stack spacing={3}>
                            {step === 'connectionType' ? (
                                <Controller
                                    control={control}
                                    name='deliveryType'
                                    render={({ field: { value, onChange } }) => (
                                        <Select
                                            id='deliveryType'
                                            label={<Trans>Delivery Type</Trans>}
                                            required
                                            disabled={organizationMetadata.isLoading}
                                            value={value}
                                            onChange={onChange}
                                        >
                                            <MenuItem value='DATA_STUDIO'>
                                                <Trans>Data Studio</Trans>
                                            </MenuItem>
                                            <MenuItem value='S3_INTERNAL'>
                                                <Trans>S3 Internal</Trans>
                                            </MenuItem>
                                            <MenuItem value='S3_EXTERNAL'>
                                                <Trans>S3 External</Trans>
                                            </MenuItem>
                                        </Select>
                                    )}
                                />
                            ) : (
                                <>
                                    {deliveryType === 'DATA_STUDIO' && (
                                        <>
                                            <Controller
                                                control={control}
                                                name='deliveryShareName'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryShareName'
                                                        label={<Trans>Share</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name='deliveryDatabase'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryDatabase'
                                                        label={<Trans>Database</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                        </>
                                    )}

                                    {deliveryType === 'S3_INTERNAL' && (
                                        <>
                                            <Controller
                                                control={control}
                                                name='deliveryAwsInternalS3BucketArn'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryAwsInternalS3BucketArn'
                                                        label={<Trans>Bucket ARN</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name='deliveryAwsInternalSftpPubKey'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryAwsInternalSftpPubKey'
                                                        label={<Trans>SFTP Public Key</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                            <S3CommonFields
                                                control={control}
                                                isLoading={organizationMetadata.isLoading}
                                                prefix='deliveryAwsInternal'
                                            />
                                        </>
                                    )}

                                    {deliveryType === 'S3_EXTERNAL' && (
                                        <>
                                            <Controller
                                                control={control}
                                                name='deliveryAwsExternalS3BucketArn'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryAwsExternalS3BucketArn'
                                                        label={<Trans>Bucket ARN</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name='deliveryAwsExternalIamArn'
                                                render={({ field: { value, onChange } }) => (
                                                    <TextField
                                                        id='deliveryAwsExternalIamArn'
                                                        label={<Trans>IAM ARN</Trans>}
                                                        required
                                                        disabled={organizationMetadata.isLoading}
                                                        value={value}
                                                        onChange={onChange}
                                                    />
                                                )}
                                            />
                                            <S3CommonFields
                                                control={control}
                                                isLoading={organizationMetadata.isLoading}
                                                prefix='deliveryAwsExternal'
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant='text' color='primary' onClick={handleCancel} disabled={organizationMetadata.isLoading}>
                        <Trans>Cancel</Trans>
                    </Button>
                    {step === 'connectionType' ? (
                        <Button variant='contained' onClick={handleNext} disabled={organizationMetadata.isLoading}>
                            <Trans>Next</Trans>
                        </Button>
                    ) : (
                        <>
                            <Button variant='text' onClick={handleBack} disabled={organizationMetadata.isLoading}>
                                <Trans>Back</Trans>
                            </Button>
                            <Button variant='contained' type='submit' disabled={organizationMetadata.isLoading}>
                                {hasExistingDataForType(deliveryType) ? <Trans>Update</Trans> : <Trans>Create</Trans>}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    )
}
